import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listPaymentMethods,
  listBonusOffers,
  listBankAccounts,
  listUpiAccounts,
  setPrimaryBankAccount as setPrimaryBankAccountService,
  submitDeposit as submitDepositService,
  initiateDeposit as initiateDepositService,
  abandonDeposit as abandonDepositService,
  submitWithdraw as submitWithdrawService,
  addBankAccount as addBankAccountService,
  addUpiAccount as addUpiAccountService,
  removeBankAccount as removeBankAccountService,
  removeUpiAccount as removeUpiAccountService,
} from "../../api/services/payment";
import { normalizePaymentMethods } from "../../utils/normalizePaymentMethod";
import { normalizeBonusOffers } from "../../utils/normalizeBonusOffer";
import { normalizeBankAccounts } from "../../utils/normalizeBankAccount";
import { normalizeUpiAccounts } from "../../utils/normalizeUpiAccount";
import { startLoading, stopLoading } from "./LoadingSlice";
import api from "../../api";
// Define the async thunk action creator
export const addBankDetails = createAsyncThunk(
  "payment/addBank",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await addBankAccountService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const addUpiDetails = createAsyncThunk(
  "payment/addUpi",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await addUpiAccountService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const removeBankDetails = createAsyncThunk(
  "payment/removeBank",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const bankAccountId = payload?.bankId || payload?.bankAccountId;
      const response = await removeBankAccountService(bankAccountId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const removeUpiDetails = createAsyncThunk(
  "payment/removeUpi",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const upiAccountId = payload?.upiAccountId || payload?.upiId;
      const response = await removeUpiAccountService(upiAccountId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const getPaymentMethods = createAsyncThunk(
  "payment/getpaymentMethods",
  async (flow = "deposit", { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await listPaymentMethods({ flow });
      return normalizePaymentMethods(response?.data);
    } catch (error) {
      throw rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const getBonusOffers = createAsyncThunk(
  "payment/getBonusOffers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listBonusOffers();
      return normalizeBonusOffers(response?.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    }
  }
);
export const getBankAccounts = createAsyncThunk(
  "payment/getBankAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listBankAccounts();
      return normalizeBankAccounts(response?.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    }
  }
);
export const getUpiAccounts = createAsyncThunk(
  "payment/getUpiAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listUpiAccounts();
      return normalizeUpiAccounts(response?.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    }
  }
);
export const setPrimaryBankAccount = createAsyncThunk(
  "payment/setPrimaryBankAccount",
  async (bankId, { dispatch, rejectWithValue }) => {
    try {
      const response = await setPrimaryBankAccountService(bankId);
      await dispatch(getBankAccounts());
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    }
  }
);
export const createDepositRequest = createAsyncThunk(
  "payment/createDepositRequest",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    let newPayload = { ...payload };
    newPayload.amount = payload.amount.replace(/,/g, "");
    try {
      const response = await api.post(
        `/payment/createDepositRequest`,
        newPayload
      );
      return response.data;
    } catch (error) {
      // Handle error condition
      throw rejectWithValue(error.response.data);
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const submitDeposit = createAsyncThunk(
  "payment/submitDeposit",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await submitDepositService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
// deposit intent tracking is best-effort analytics for support: it never shows
// a loader or an error to the player
export const initiateDepositIntent = createAsyncThunk(
  "payment/initiateDepositIntent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await initiateDepositService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);
export const abandonDepositIntent = createAsyncThunk(
  "payment/abandonDepositIntent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await abandonDepositService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);
export const createWithdrawRequest = createAsyncThunk(
  "payment/createWithdrawRequest",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await submitWithdrawService({
        bankAccountId: payload.bankId,
        amount: payload.amount,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Invalid Request" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
// Create a slice for your API-related actions and reducers
const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    error: null,
    bankDetails: null,
    upiDetails: null,
    paymentMethods: [],
    bonusOffers: [],
    bankAccounts: [],
    upiAccounts: [],
    depositRequestedSuccessfully: false,
    withrawRequestedSuccessfully: false,
    depositMessage: "",
    withdrawMessage: "",
    selectedAmount: {},
    bankRemoved: false,
    upiRemoved: false,
    transactionUrl: "",
    // open deposit intent: { id, amount, paymentMethodId }
    depositIntent: null,
  },
  reducers: {
    reset(state, action) {
      state.depositRequestedSuccessfully = false;
      state.withrawRequestedSuccessfully = false;
      state.error = null;
      state.selectedAmount = action.payload;
      state.bankRemoved = false;
      state.upiRemoved = false;
      state.withdrawMessage = "";
      state.depositMessage = "";
      state.bankDetails = null;
      state.upiDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addBankDetails.fulfilled, (state) => {
        state.error = null;
        state.bankDetails = true;
      })
      .addCase(addBankDetails.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      })
      .addCase(addUpiDetails.fulfilled, (state) => {
        state.error = null;
        state.upiDetails = true;
      })
      .addCase(addUpiDetails.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      })
      .addCase(removeBankDetails.fulfilled, (state, action) => {
        state.error = null;
        state.bankRemoved =
          action.payload?.success === true || action.payload?.status == 200;
      })
      .addCase(removeBankDetails.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      })
      .addCase(removeUpiDetails.fulfilled, (state, action) => {
        state.error = null;
        state.upiRemoved =
          action.payload?.success === true || action.payload?.status == 200;
      })
      .addCase(removeUpiDetails.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethods = action.payload;
      })
      .addCase(getPaymentMethods.rejected, (state, action) => {
        state.error = action.error.message || "Invalid Request";
      })
      .addCase(getBonusOffers.fulfilled, (state, action) => {
        state.bonusOffers = action.payload;
      })
      .addCase(getBonusOffers.rejected, (state) => {
        state.bonusOffers = [];
      })
      .addCase(getBankAccounts.fulfilled, (state, action) => {
        state.bankAccounts = action.payload;
        state.error = null;
      })
      .addCase(getBankAccounts.rejected, (state, action) => {
        state.bankAccounts = [];
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Unable to load bank accounts";
      })
      .addCase(getUpiAccounts.fulfilled, (state, action) => {
        state.upiAccounts = action.payload;
        state.error = null;
      })
      .addCase(getUpiAccounts.rejected, (state, action) => {
        state.upiAccounts = [];
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Unable to load UPI accounts";
      })
      .addCase(setPrimaryBankAccount.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(setPrimaryBankAccount.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Unable to set primary bank account";
      })
      .addCase(createDepositRequest.fulfilled, (state, action) => {
        state.depositMessage =
          action.payload.data.message ||
          "Deposit Request Accepted Successfully";
        state.depositRequestedSuccessfully = action.payload.status == 200;
        state.transactionUrl = action.payload.data.url || "";
      })
      .addCase(createDepositRequest.rejected, (state, action) => {
        state.depositMessage = "";
        state.error = action.payload.error || "Invalid Request";
      })
      .addCase(initiateDepositIntent.fulfilled, (state, action) => {
        const id = action.payload?.data?.id;
        state.depositIntent = id
          ? {
              id,
              amount: action.meta.arg.amount,
              paymentMethodId: action.meta.arg.paymentMethodId,
            }
          : null;
      })
      .addCase(abandonDepositIntent.fulfilled, (state) => {
        state.depositIntent = null;
      })
      .addCase(submitDeposit.fulfilled, (state, action) => {
        // any deposit converts the open intent on the server
        state.depositIntent = null;
        state.error = null;
        state.depositMessage =
          action.payload?.message ||
          action.payload?.data?.message ||
          "Deposit Request Accepted Successfully";
        state.depositRequestedSuccessfully = true;
      })
      .addCase(submitDeposit.rejected, (state, action) => {
        state.depositMessage = "";
        state.depositRequestedSuccessfully = false;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      })
      .addCase(createWithdrawRequest.fulfilled, (state, action) => {
        state.error = null;
        state.withdrawMessage =
          action.payload?.message ||
          action.payload?.data?.message ||
          "Withdraw Request Accepted Successfully";
        state.withrawRequestedSuccessfully = true;
      })
      .addCase(createWithdrawRequest.rejected, (state, action) => {
        state.withdrawMessage = "";
        state.withrawRequestedSuccessfully = false;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          action.payload?.detail ||
          "Invalid Request";
      });
  },
});

// Export the async thunk action creator and the reducer
export const { reducer } = paymentSlice;
export const { reset } = paymentSlice.actions;
export default paymentSlice.reducer;
